/**
 * AdminMessagesPage.tsx
 *
 * Owner-only inbox for all teacher feedback, support requests, bug reports,
 * and feature ideas submitted via the Help & Feedback button.
 *
 * Access is gated server-side by the ADMIN_USER_ID env var. This page handles
 * the 403 gracefully with setup instructions if the var is not yet configured.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Search, RefreshCw, MessageCircle, HelpCircle, Bug,
  Lightbulb, Headphones, ChevronDown, ChevronUp, Send, Check,
  AlertCircle, Paperclip, Clock, Mail, Globe, Shield,
  CheckCircle2, XCircle, Eye, Inbox,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ── Types ─────────────────────────────────────────────────────────────────────

type MsgStatus = 'new' | 'in_review' | 'responded' | 'closed';
type MsgCategory = 'question' | 'bug' | 'feature' | 'support';

interface AdminMessage {
  id: number;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  category: MsgCategory;
  message: string;
  current_page: string | null;
  resource_type: string | null;
  resource_id: string | null;
  browser_info: string | null;
  status: MsgStatus;
  admin_reply: string | null;
  replied_at: string | null;
  created_at: string;
  has_attachment: boolean;
}

interface MessagesResponse {
  messages: AdminMessage[];
  total: number;
  byStatus: Record<string, number>;
}

// ── Config ────────────────────────────────────────────────────────────────────

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
const API = `${BASE}/api/admin`;

// ── Helpers ───────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<MsgCategory, { label: string; icon: React.ElementType; color: string; bg: string; ring: string }> = {
  question: { label: 'Question',        icon: HelpCircle, color: 'text-teal-600',  bg: 'bg-teal-50',  ring: 'ring-teal-200' },
  bug:      { label: 'Bug Report',      icon: Bug,        color: 'text-red-500',   bg: 'bg-red-50',   ring: 'ring-red-200' },
  feature:  { label: 'Feature Request', icon: Lightbulb,  color: 'text-amber-500', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  support:  { label: 'Support',         icon: Headphones, color: 'text-blue-500',  bg: 'bg-blue-50',  ring: 'ring-blue-200' },
};

const STATUS_META: Record<MsgStatus, { label: string; color: string; bg: string; dot: string }> = {
  new:       { label: 'New',        color: 'text-violet-700', bg: 'bg-violet-100', dot: 'bg-violet-500' },
  in_review: { label: 'In Review',  color: 'text-blue-700',   bg: 'bg-blue-100',   dot: 'bg-blue-500' },
  responded: { label: 'Responded',  color: 'text-emerald-700',bg: 'bg-emerald-100',dot: 'bg-emerald-500' },
  closed:    { label: 'Closed',     color: 'text-slate-500',  bg: 'bg-slate-100',  dot: 'bg-slate-400' },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Attachment modal ──────────────────────────────────────────────────────────

function AttachmentView({ messageId, onClose }: { messageId: number; onClose: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetch(`${API}/messages/${messageId}/attachment`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((d: any) => setSrc(d.attachment))
      .catch(() => setErr(true));
  }, [messageId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-4 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-700">Screenshot</p>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <XCircle size={18} />
          </button>
        </div>
        {!src && !err && (
          <div className="h-48 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {err && <p className="text-sm text-red-500 text-center py-8">Failed to load attachment.</p>}
        {src && <img src={src} alt="Attachment" className="w-full rounded-xl border border-slate-100" />}
      </div>
    </div>
  );
}

// ── Message card ──────────────────────────────────────────────────────────────

function MessageCard({
  msg,
  onStatusChange,
  onReplySent,
}: {
  msg: AdminMessage;
  onStatusChange: (id: number, status: MsgStatus) => void;
  onReplySent: (id: number, reply: string, repliedAt: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySent, setReplySent] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAttachment, setShowAttachment] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const catMeta = CATEGORY_META[msg.category] ?? CATEGORY_META.support;
  const statusMeta = STATUS_META[msg.status] ?? STATUS_META.new;
  const CatIcon = catMeta.icon;

  async function handleStatusChange(newStatus: MsgStatus) {
    if (newStatus === msg.status || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const r = await fetch(`${API}/messages/${msg.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) throw new Error();
      onStatusChange(msg.id, newStatus);
    } catch {
      // status reverts silently
    } finally {
      setUpdatingStatus(false);
    }
  }

  async function handleSendReply() {
    if (!replyText.trim() || replyText.trim().length < 5 || sending) return;
    setSending(true);
    setReplyError(null);
    try {
      const r = await fetch(`${API}/messages/${msg.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ replyText: replyText.trim() }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.error ?? 'Send failed');
      setReplySent(true);
      onReplySent(msg.id, replyText.trim(), body.replied_at);
    } catch (err) {
      setReplyError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  // When expanded and unread, mark as in_review
  useEffect(() => {
    if (expanded && msg.status === 'new') {
      handleStatusChange('in_review');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  return (
    <>
      {showAttachment && (
        <AttachmentView messageId={msg.id} onClose={() => setShowAttachment(false)} />
      )}
      <div
        className={`bg-white rounded-2xl border transition-all duration-200 ${
          msg.status === 'new'
            ? 'border-violet-200 shadow-sm shadow-violet-100'
            : 'border-slate-200 shadow-sm'
        }`}
      >
        {/* Card header — always visible */}
        <div
          className="flex items-start gap-3 p-4 cursor-pointer select-none"
          onClick={() => setExpanded(e => !e)}
        >
          {/* Category icon */}
          <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${catMeta.bg} ring-1 ${catMeta.ring}`}>
            <CatIcon size={16} className={catMeta.color} />
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-900 truncate">
                {msg.user_name ?? 'Anonymous'}
              </span>
              {msg.user_email && (
                <span className="text-xs text-slate-400 truncate">{msg.user_email}</span>
              )}
              <span className={`ml-auto flex-shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusMeta.bg} ${statusMeta.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                {statusMeta.label}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{msg.message}</p>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
              <span className={`font-medium ${catMeta.color}`}>{catMeta.label}</span>
              {msg.current_page && (
                <span className="flex items-center gap-1"><Globe size={11} />{msg.current_page}</span>
              )}
              {msg.has_attachment && (
                <span className="flex items-center gap-1"><Paperclip size={11} />Screenshot</span>
              )}
              <span className="flex items-center gap-1 ml-auto"><Clock size={11} />{timeAgo(msg.created_at)}</span>
            </div>
          </div>

          {/* Expand chevron */}
          <div className="flex-shrink-0 text-slate-400 mt-1">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {/* Expanded content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4">

                {/* Full message */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Message</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                </div>

                {/* Context details */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {msg.user_email && (
                    <a href={`mailto:${msg.user_email}`}
                       className="flex items-center gap-1.5 text-slate-500 hover:text-primary transition-colors">
                      <Mail size={12} className="flex-shrink-0" />
                      <span className="truncate">{msg.user_email}</span>
                    </a>
                  )}
                  {msg.current_page && (
                    <span className="flex items-center gap-1.5 text-slate-500">
                      <Globe size={12} className="flex-shrink-0" />
                      <span className="truncate">{msg.current_page}</span>
                    </span>
                  )}
                  {msg.browser_info && (
                    <span className="col-span-2 flex items-start gap-1.5 text-slate-400">
                      <Eye size={12} className="flex-shrink-0 mt-0.5" />
                      <span className="truncate">{msg.browser_info}</span>
                    </span>
                  )}
                </div>

                {/* Attachment */}
                {msg.has_attachment && (
                  <button
                    onClick={() => setShowAttachment(true)}
                    className="flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    <Paperclip size={13} /> View attached screenshot
                  </button>
                )}

                {/* Status controls */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400 mr-1">Status:</span>
                  {(Object.keys(STATUS_META) as MsgStatus[]).map(s => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      disabled={updatingStatus}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all disabled:opacity-50 ${
                        msg.status === s
                          ? `${STATUS_META[s].bg} ${STATUS_META[s].color} border-transparent font-semibold`
                          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {msg.status === s && <Check size={10} />}
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>

                {/* Previous reply */}
                {msg.admin_reply && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-emerald-600 mb-1.5 flex items-center gap-1">
                      <CheckCircle2 size={12} /> Your reply
                      {msg.replied_at && (
                        <span className="font-normal text-emerald-500 ml-1">· {timeAgo(msg.replied_at)}</span>
                      )}
                    </p>
                    <p className="text-sm text-emerald-800 whitespace-pre-wrap leading-relaxed">{msg.admin_reply}</p>
                  </div>
                )}

                {/* Reply form */}
                {!replySent && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500">
                      {msg.admin_reply ? 'Send another reply' : 'Reply to teacher'}
                      {msg.user_email ? (
                        <span className="font-normal text-slate-400"> — will be emailed to {msg.user_email}</span>
                      ) : (
                        <span className="font-normal text-amber-500"> — no email address on file</span>
                      )}
                    </p>
                    <textarea
                      ref={textareaRef}
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={4}
                      placeholder="Type your reply…"
                      className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm
                        text-slate-800 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-primary/20
                        focus:border-primary/40 focus:bg-white transition-all"
                    />
                    {replyError && (
                      <p className="text-xs text-red-600 flex items-center gap-1.5 bg-red-50 rounded-lg px-3 py-2">
                        <AlertCircle size={12} /> {replyError}
                      </p>
                    )}
                    <div className="flex justify-end">
                      <Button
                        onClick={handleSendReply}
                        disabled={replyText.trim().length < 5 || sending}
                        className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-2 disabled:opacity-40 h-9 text-sm"
                      >
                        {sending ? (
                          <>
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                            Sending…
                          </>
                        ) : (
                          <><Send size={14} /> {msg.user_email ? 'Send Reply' : 'Save Note'}</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {replySent && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-xl px-4 py-3">
                    <CheckCircle2 size={16} />
                    Reply saved{msg.user_email ? ' and emailed to the teacher.' : '.'}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function AdminMessagesPage({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<MessagesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [setupHint, setSetupHint] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const load = useCallback(async (opts?: { search?: string; category?: string; status?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: '100',
        offset: '0',
        category: opts?.category ?? category,
        status: opts?.status ?? statusFilter,
      });
      const q = opts?.search ?? search;
      if (q) params.set('search', q);

      const r = await fetch(`${API}/messages?${params}`);
      if (r.status === 403) {
        const body = await r.json();
        setAccessDenied(true);
        if (body?.hint) setError(body.hint);
        return;
      }
      if (!r.ok) throw new Error('Failed to load messages');
      setData(await r.json());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [category, statusFilter, search]);

  useEffect(() => { load(); }, []);

  // Also check /admin/check to show setup hint if admin not configured
  useEffect(() => {
    fetch(`${API}/check`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (d && !d.configured && d.yourUserId) {
          setSetupHint(d.yourUserId);
        }
      })
      .catch(() => {});
  }, []);

  function handleSearchChange(val: string) {
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      load({ search: val });
    }, 400);
  }

  function handleCategoryChange(cat: string) {
    setCategory(cat);
    load({ category: cat });
  }

  function handleStatusFilterChange(s: string) {
    setStatusFilter(s);
    load({ status: s });
  }

  function handleStatusChange(id: number, status: MsgStatus) {
    setData(prev => {
      if (!prev) return prev;
      const msgs = prev.messages.map(m => m.id === id ? { ...m, status } : m);
      const byStatus = { ...prev.byStatus };
      const old = prev.messages.find(m => m.id === id);
      if (old) {
        byStatus[old.status] = Math.max((byStatus[old.status] ?? 0) - 1, 0);
        byStatus[status] = (byStatus[status] ?? 0) + 1;
      }
      return { ...prev, messages: msgs, byStatus };
    });
  }

  function handleReplySent(id: number, reply: string, repliedAt: string) {
    handleStatusChange(id, 'responded');
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map(m =>
          m.id === id ? { ...m, admin_reply: reply, replied_at: repliedAt, status: 'responded' } : m
        ),
      };
    });
  }

  const newCount = data?.byStatus?.new ?? 0;
  const totalCount = data?.total ?? 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2.5 flex-1">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shield size={15} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">Admin Messages</p>
              <p className="text-xs text-slate-400">
                {totalCount} total{newCount > 0 && ` · ${newCount} new`}
              </p>
            </div>
          </div>
          <button
            onClick={() => load()}
            className="p-2 rounded-xl text-slate-400 hover:text-primary hover:bg-primary/5 transition-all"
            title="Refresh"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">

        {/* Setup notice — ADMIN_USER_ID not configured */}
        {setupHint && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <AlertCircle size={15} /> Admin access not configured
            </p>
            <p className="text-sm text-amber-700">
              Set <code className="bg-amber-100 px-1 rounded font-mono text-xs">ADMIN_USER_ID</code> to{' '}
              <code className="bg-amber-100 px-1 rounded font-mono text-xs">{setupHint}</code> in the environment secrets.
              Then also set{' '}
              <code className="bg-amber-100 px-1 rounded font-mono text-xs">ADMIN_NOTIFICATION_EMAIL</code> to your email address to receive alerts when teachers submit messages.
            </p>
          </div>
        )}

        {/* Access denied */}
        {accessDenied && !setupHint && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center space-y-2">
            <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center mx-auto">
              <Shield size={22} className="text-red-500" />
            </div>
            <p className="text-sm font-semibold text-red-800">Admin access required</p>
            <p className="text-xs text-red-600">
              {error ?? 'Set the ADMIN_USER_ID environment secret to your Clerk user ID.'}
            </p>
          </div>
        )}

        {/* Stats bar */}
        {data && (
          <div className="grid grid-cols-4 gap-3">
            {[
              { key: 'new',       label: 'New',        icon: Inbox,         color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-200' },
              { key: 'in_review', label: 'In Review',  icon: Eye,           color: 'text-blue-600',   bg: 'bg-blue-50',   ring: 'ring-blue-200' },
              { key: 'responded', label: 'Responded',  icon: CheckCircle2,  color: 'text-emerald-600',bg: 'bg-emerald-50',ring: 'ring-emerald-200' },
              { key: 'closed',    label: 'Closed',     icon: XCircle,       color: 'text-slate-500',  bg: 'bg-slate-50',  ring: 'ring-slate-200' },
            ].map(({ key, label, icon: Icon, color, bg, ring }) => (
              <button
                key={key}
                onClick={() => handleStatusFilterChange(statusFilter === key ? 'all' : key)}
                className={`rounded-2xl border p-3 text-left transition-all ${
                  statusFilter === key ? `${bg} ring-1 ${ring}` : 'bg-white hover:bg-slate-50'
                } border-slate-200`}
              >
                <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center mb-1.5`}>
                  <Icon size={14} className={color} />
                </div>
                <p className="text-lg font-bold text-slate-900 leading-none">{data.byStatus?.[key] ?? 0}</p>
                <p className="text-xs text-slate-500 mt-0.5">{label}</p>
              </button>
            ))}
          </div>
        )}

        {/* Search + filters */}
        {!accessDenied && (
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search name, email, or message…"
                className="w-full h-9 pl-8 pr-3 rounded-xl border border-slate-200 bg-white text-sm placeholder:text-slate-400
                  outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
              />
            </div>
            <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
              {(['all', 'question', 'bug', 'feature', 'support'] as const).map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                    category === cat
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {cat === 'all' ? 'All' : CATEGORY_META[cat as MsgCategory].label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !data && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-100 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-slate-100 rounded w-1/3" />
                    <div className="h-3 bg-slate-100 rounded w-4/5" />
                    <div className="h-3 bg-slate-100 rounded w-2/5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && !accessDenied && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && data && data.messages.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={24} className="text-slate-400" />
            </div>
            <p className="text-base font-semibold text-slate-700 mb-1">No messages yet</p>
            <p className="text-sm text-slate-400">
              {search || category !== 'all' || statusFilter !== 'all'
                ? 'No messages match your current filters.'
                : 'Teacher messages submitted via the Help & Feedback button will appear here.'}
            </p>
          </div>
        )}

        {/* Message list */}
        {data && data.messages.length > 0 && (
          <div className="space-y-3">
            {data.messages.map(msg => (
              <MessageCard
                key={msg.id}
                msg={msg}
                onStatusChange={handleStatusChange}
                onReplySent={handleReplySent}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
